import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Plus, GripVertical, Edit, Trash2, Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SubtaskDialog } from "./SubtaskDialog";
import { SubtaskDetailModal } from "./SubtaskDetailModal";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from "@/lib/utils";

interface Subtask {
  id: string;
  title: string;
  description?: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked';
  assignee_id?: string;
  assignee_name?: string;
  due_date?: string;
  order_number: number;
}

interface SubtaskListProps {
  taskId: string;
  unaId: string | null;
}

function SortableSubtask({ subtask, onToggle, onEdit, onDelete, onOpenDetail }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: subtask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const statusColors = {
    not_started: "border-muted hover:border-primary/50",
    in_progress: "border-blue-500 bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900",
    completed: "border-green-500 bg-green-50 dark:bg-green-950 hover:bg-green-100 dark:hover:bg-green-900",
    blocked: "border-destructive bg-destructive/10 hover:bg-destructive/20",
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={cn(
        "border rounded-lg p-3 cursor-pointer transition-all", 
        statusColors[subtask.status]
      )}
      onClick={(e) => {
        // Don't open if clicking on interactive elements
        if (
          (e.target as HTMLElement).closest('button') ||
          (e.target as HTMLElement).closest('[role="checkbox"]') ||
          (e.target as HTMLElement).getAttribute('data-drag-handle') === 'true'
        ) {
          return;
        }
        onOpenDetail(subtask);
      }}
    >
      <div className="flex items-start gap-3">
        <div 
          {...attributes} 
          {...listeners} 
          data-drag-handle="true"
          className="cursor-grab active:cursor-grabbing mt-1"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        
        <Checkbox
          checked={subtask.status === 'completed'}
          onCheckedChange={() => onToggle(subtask.id)}
          className="mt-1"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className={cn("font-medium", subtask.status === 'completed' && "line-through text-muted-foreground")}>
                {subtask.title}
              </p>
              {subtask.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{subtask.description}</p>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(subtask);
                }}
              >
                <Edit className="h-3 w-3" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(subtask.id);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-2">
            {subtask.assignee_name && (
              <Badge variant="outline" className="text-xs gap-1">
                <User className="h-3 w-3" />
                {subtask.assignee_name}
              </Badge>
            )}
            {subtask.due_date && (
              <Badge variant="outline" className="text-xs gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(subtask.due_date), 'MMM d')}
              </Badge>
            )}
            {subtask.status !== 'completed' && subtask.status !== 'not_started' && (
              <Badge variant="secondary" className="text-xs">
                {subtask.status.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SubtaskList({ taskId, unaId }: SubtaskListProps) {
  const { toast } = useToast();
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState<Subtask | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [viewingSubtask, setViewingSubtask] = useState<Subtask | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadSubtasks = async () => {
    try {
      const { data, error } = await supabase
        .from('task_subtasks')
        .select('*')
        .eq('task_id', taskId)
        .order('order_number', { ascending: true });

      if (error) throw error;

      // Load assignee names separately
      const subtasksWithNames = await Promise.all(
        (data || []).map(async (sub) => {
          if (sub.assignee_id) {
            const { data: member } = await supabase
              .from('una_members')
              .select('full_name')
              .eq('id', sub.assignee_id)
              .single();
            
            return {
              ...sub,
              assignee_name: member?.full_name,
              status: sub.status as 'not_started' | 'in_progress' | 'completed' | 'blocked',
            };
          }
          return {
            ...sub,
            status: sub.status as 'not_started' | 'in_progress' | 'completed' | 'blocked',
          };
        })
      );

      setSubtasks(subtasksWithNames);
    } catch (error) {
      console.error('Error loading subtasks:', error);
      toast({
        title: "Error",
        description: "Failed to load subtasks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubtasks();
  }, [taskId]);

  const handleToggle = async (subtaskId: string) => {
    const subtask = subtasks.find(s => s.id === subtaskId);
    if (!subtask) return;

    const newStatus = subtask.status === 'completed' ? 'not_started' : 'completed';

    try {
      const { error } = await supabase
        .from('task_subtasks')
        .update({ status: newStatus })
        .eq('id', subtaskId);

      if (error) throw error;

      await loadSubtasks();
      toast({
        title: "Subtask Updated",
        description: newStatus === 'completed' ? "Subtask completed!" : "Subtask reopened",
      });
    } catch (error) {
      console.error('Error toggling subtask:', error);
      toast({
        title: "Error",
        description: "Failed to update subtask",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = subtasks.findIndex(s => s.id === active.id);
    const newIndex = subtasks.findIndex(s => s.id === over.id);

    const newSubtasks = arrayMove(subtasks, oldIndex, newIndex);
    setSubtasks(newSubtasks);

    // Update order in database
    try {
      const updates = newSubtasks.map((sub, idx) => ({
        id: sub.id,
        order_number: idx,
      }));

      for (const update of updates) {
        await supabase
          .from('task_subtasks')
          .update({ order_number: update.order_number })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating order:', error);
      await loadSubtasks(); // Reload on error
    }
  };

  const handleDelete = async (subtaskId: string) => {
    if (!confirm('Are you sure you want to delete this subtask?')) return;

    try {
      const { error } = await supabase
        .from('task_subtasks')
        .delete()
        .eq('id', subtaskId);

      if (error) throw error;

      await loadSubtasks();
      toast({
        title: "Subtask Deleted",
        description: "Subtask removed successfully",
      });
    } catch (error) {
      console.error('Error deleting subtask:', error);
      toast({
        title: "Error",
        description: "Failed to delete subtask",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    setDialogOpen(false);
    setEditingSubtask(null);
    await loadSubtasks();
  };

  const completedCount = subtasks.filter(s => s.status === 'completed').length;
  const progress = subtasks.length > 0 ? (completedCount / subtasks.length) * 100 : 0;

  if (loading) return <div className="text-center py-4">Loading subtasks...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Subtasks ({completedCount}/{subtasks.length})</h3>
          <Progress value={progress} className="h-2 w-48 mt-2" />
        </div>
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Subtask
        </Button>
      </div>

      {subtasks.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          <p>No subtasks yet. Break this task into smaller steps!</p>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={subtasks.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {subtasks.map(subtask => (
                <SortableSubtask
                  key={subtask.id}
                  subtask={subtask}
                  onToggle={handleToggle}
                  onEdit={(sub: Subtask) => {
                    setEditingSubtask(sub);
                    setDialogOpen(true);
                  }}
                  onDelete={handleDelete}
                  onOpenDetail={(sub: Subtask) => {
                    setViewingSubtask(sub);
                    setDetailModalOpen(true);
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <SubtaskDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingSubtask(null);
        }}
        taskId={taskId}
        unaId={unaId}
        subtask={editingSubtask}
        onSave={handleSave}
      />

      {viewingSubtask && (
        <SubtaskDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setViewingSubtask(null);
          }}
          subtaskId={viewingSubtask.id}
          taskId={taskId}
          unaId={unaId}
          onUpdate={loadSubtasks}
          onEdit={() => {
            setDetailModalOpen(false);
            setEditingSubtask(viewingSubtask);
            setDialogOpen(true);
          }}
        />
      )}
    </div>
  );
}
